import { useSyncExternalStore } from "react";

/**
 * The Telegram Mini App bridge (ADR-0009). Telegram opens the store with `#tgWebAppData=…` in the URL; only then do
 * we load Telegram's script, so web visitors never download it. Everything Telegram-specific stays in this folder:
 * features call `@/shared/platform`, never `window.Telegram`.
 */

/** The part of Telegram's `window.Telegram.WebApp` we use (https://core.telegram.org/bots/webapps). */
export interface TelegramWebApp {
  initData: string;
  platform: string;
  ready: () => void;
  expand: () => void;
  close: () => void;
  disableVerticalSwipes?: () => void;
  setHeaderColor: (color: string) => void;
  setBackgroundColor: (color: string) => void;
  openLink: (url: string) => void;
  BackButton: {
    show: () => void;
    hide: () => void;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
  };
  HapticFeedback?: {
    impactOccurred: (style: "light" | "medium") => void;
    notificationOccurred: (type: "success" | "error" | "warning") => void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

const SDK_URL = "https://telegram.org/js/telegram-web-app.js";
const HASH_KEY = "tgWebAppData";
const SESSION_KEY = "ishonch.tma";

/**
 * True when the page was opened inside Telegram. The launch hash is read once at startup (the router may drop it),
 * and remembered for the tab so a reload inside Telegram still counts.
 */
export const launchedInTelegram: boolean = detect();

function detect(): boolean {
  if (typeof window === "undefined") return false;
  const fromHash = new URLSearchParams(window.location.hash.slice(1)).has(HASH_KEY);
  try {
    if (fromHash) sessionStorage.setItem(SESSION_KEY, "1");
    return fromHash || sessionStorage.getItem(SESSION_KEY) === "1";
  } catch {
    return fromHash;
  }
}

/** Telegram's object once its script has run; undefined on the web. */
export function telegramApp(): TelegramWebApp | undefined {
  return launchedInTelegram ? window.Telegram?.WebApp : undefined;
}

let loading: Promise<TelegramWebApp | undefined> | undefined;
const readyListeners = new Set<() => void>();

/** Telegram's object as React state: undefined until its script has loaded (and always on the web). */
export function useTelegramApp(): TelegramWebApp | undefined {
  return useSyncExternalStore(
    (onChange) => {
      readyListeners.add(onChange);
      return () => {
        readyListeners.delete(onChange);
      };
    },
    telegramApp,
    () => undefined,
  );
}

/** Loads Telegram's script (only inside Telegram) and prepares the app: full height, light header, no swipe-close. */
export function loadTelegram(): Promise<TelegramWebApp | undefined> {
  if (!launchedInTelegram) return Promise.resolve(undefined);
  loading ??= new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = SDK_URL;
    script.async = true;
    script.onload = () => {
      const app = window.Telegram?.WebApp;
      // the script has read the launch data: drop it from the address (initData stays available in the script)
      dropLaunchData();
      if (app) {
        app.ready();
        app.expand();
        app.disableVerticalSwipes?.();
        // the store keeps its light brand look whatever the Telegram theme is (D39)
        const css = getComputedStyle(document.documentElement);
        app.setHeaderColor(css.getPropertyValue("--color-surface").trim() || "#ffffff");
        app.setBackgroundColor(css.getPropertyValue("--color-canvas").trim() || "#f1f5f9");
        document.documentElement.dataset.telegram = "true";
      }
      resolve(app);
      readyListeners.forEach((listener) => {
        listener();
      });
    };
    script.onerror = () => {
      dropLaunchData();
      resolve(undefined);
    };
    document.head.append(script);
  });
  return loading;
}

/** A copied or shared link must never carry the customer's signed sign-in data (`#tgWebAppData=…`). */
function dropLaunchData(): void {
  if (new URLSearchParams(window.location.hash.slice(1)).has(HASH_KEY)) {
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname + window.location.search,
    );
  }
}
